import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, ShieldCheck, Mail, User as UserIcon, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { apiCall } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface User {
    id: number;
    username: string;
    name?: string;
    email?: string;
    roles: Role[];
}

interface Role {
    id: number;
    name: string;
}

const AdminUsersPage: React.FC = () => {
    const { token } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);

    const [formData, setFormData] = useState({
        username: "",
        name: "",
        email: "",
        password: "",
        role: ""
    });

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [token]);

    const fetchUsers = async () => {
        if (!token) return;
        try {
            const data: any = await apiCall(token, '/admin/users');
            setUsers(data);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Erreur lors du chargement des utilisateurs");
        }
    };

    const fetchRoles = async () => {
        if (!token) return;
        try {
            const data: any = await apiCall(token, '/roles');
            setRoles(data);
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    const handleOpenDialog = (user?: User) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                name: user.name || "",
                email: user.email || "",
                password: "", // Don't show password
                role: user.roles.length > 0 ? user.roles[0].name : ""
            });
        } else {
            setEditingUser(null);
            setFormData({ username: "", name: "", email: "", password: "", role: "" });
        }
        setIsDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!token) return;

        const payload: any = {
            username: formData.username,
            name: formData.name || undefined,
            email: formData.email || undefined,
            roles: formData.role ? [formData.role] : []
        };
        if (formData.password) {
            payload.password = formData.password;
        }

        try {
            if (editingUser) {
                await apiCall(token, `/admin/users/${editingUser.id}`, 'PUT', payload);
                toast.success("Utilisateur mis à jour");
            } else {
                if (!formData.password) {
                    toast.error("Le mot de passe est requis pour un nouvel utilisateur");
                    return;
                }
                await apiCall(token, '/admin/users', 'POST', payload);
                toast.success("Utilisateur créé");
            }
            setIsDialogOpen(false);
            fetchUsers();
        } catch (error) {
            console.error("Error saving user:", error);
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const handleDelete = async (id: number) => {
        if (!token || !confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;
        try {
            await apiCall(token, `/admin/users/${id}`, 'DELETE');
            toast.success("Utilisateur supprimé");
            fetchUsers();
        } catch (error: any) {
            console.error("Error deleting user:", error);
            toast.error("Erreur lors de la suppression");
        }
    };

    return (
        <div className="container mx-auto py-8 px-4 space-y-8 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-2xl">
                            <ShieldCheck className="h-6 w-6 text-primary" />
                        </div>
                        <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Utilisateurs Système</h1>
                    </div>
                    <p className="text-slate-500 font-medium ml-14">Gérez l'accès à la plateforme et les rôles administratifs.</p>
                </div>
                <div className="flex items-center gap-3 ml-14 md:ml-0">
                    <Button
                        onClick={() => handleOpenDialog()}
                        className="h-12 px-6 rounded-2xl bg-primary shadow-xl shadow-primary/20 text-white font-bold hover:scale-105 active:scale-95 transition-all text-xs uppercase tracking-widest"
                    >
                        <Plus className="mr-2 h-4 w-4" /> Nouvel Utilisateur
                    </Button>
                </div>
            </div>

            <Card className="border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-slate-50 hover:bg-transparent">
                                <TableHead className="pl-8 text-[11px] font-black uppercase tracking-widest text-slate-400">Identité / Utilisateur</TableHead>
                                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Rôles / Permissions</TableHead>
                                <TableHead className="w-[150px] pr-8 text-right text-[11px] font-black uppercase tracking-widest text-slate-400">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="h-64 text-center">
                                        <div className="flex flex-col items-center justify-center gap-3">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                            <p className="text-sm font-bold text-slate-400 italic text-uppercase tracking-widest">Chargement...</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => (
                                    <TableRow key={user.id} className="group border-slate-50 hover:bg-slate-50/50 transition-colors">
                                        <TableCell className="pl-8 py-5">
                                            <div className="flex items-center gap-4">
                                                <Avatar className="h-11 w-11 rounded-2xl border-2 border-white shadow-sm ring-1 ring-slate-100">
                                                    <AvatarFallback className="bg-primary/5 text-primary font-black text-xs uppercase">
                                                        {(user.name || user.username).substring(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors tracking-tight">
                                                        {user.name || user.username}
                                                    </span>
                                                    <span className="text-xs font-bold text-slate-400 tracking-widest flex items-center gap-1.5 mt-0.5 capitalize">
                                                        <Mail className="h-3 w-3" />
                                                        {user.email || user.username}
                                                    </span>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-2">
                                                {user.roles.map(r => (
                                                    <Badge key={r.id} className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-none font-semibold text-sm px-3 py-1 rounded-xl capitalize tracking-widest">
                                                        {r.name}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className="pr-8 text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(user)} className="h-9 w-9 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all">
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} className="h-9 w-9 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-md border-none shadow-2xl rounded-[2.5rem] bg-white ring-1 ring-slate-100 overflow-hidden">
                    <DialogHeader className="p-2 pt-6">
                        <DialogTitle className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                            {editingUser ? "Modifier Profil" : "Nouvel Utilisateur"}
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 font-medium">
                            Configurez les accès et l'identité de l'utilisateur.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-4 px-2">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Nom Complet</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="ex: Jean Dupont"
                                className="h-11 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="username" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Identifiant</Label>
                                <Input
                                    id="username"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                    placeholder="ex: jean.dup"
                                    className="h-11 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="ex: mail@amc.com"
                                    className="h-11 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                                Mot de passe {editingUser && "(Laisser vide pour ne pas changer)"}
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className="h-11 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Rôle Système</Label>
                            <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                                <SelectTrigger className="h-11 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20">
                                    <SelectValue placeholder="Choisir un rôle" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-slate-100">
                                    {roles.map(role => (
                                        <SelectItem key={role.id} value={role.name} className="rounded-xl font-bold">{role.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter className="p-2 gap-2">
                        <Button variant="ghost" onClick={() => setIsDialogOpen(false)} className="rounded-2xl h-11 px-6 font-bold text-slate-400">Annuler</Button>
                        <Button onClick={handleSubmit} className="rounded-2xl h-11 px-8 bg-primary shadow-lg shadow-primary/20 font-black uppercase tracking-widest text-[10px]">
                            {editingUser ? "Mettre à jour" : "Créer l'utilisateur"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUsersPage;
